import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { normalizeCurrency } from '@/lib/currency';
import { resolveStoreName } from '@/lib/display';
import { resolveMediaUrl } from '@/lib/mediaUrl';
import { buildWhatsAppUrl, cleanWhatsAppNumber } from '@/hooks/useStoreWhatsApp';
import { getOpenState, hasOpeningHours, WEEKDAYS, normalizeOpeningHours } from '@/lib/restaurantHours';
import type { Reseau } from '@/lib/networks';
import AnnonceCard from '@/components/feature/marketplace/AnnonceCard';
import { fetchVendorRatings, type VendorRating } from '@/lib/vendorReviews';
import {
  fetchSellersByIds,
  fetchVendorPublishedAnnonces,
  fetchVendorBanners,
  fetchVendorProductCategories,
  fetchMarketplaceCategories,
  fetchVendorRelation,
  incrementBannerClick,
  fetchSellerNetworks,
  fetchVendorBookingServices,
  fetchVendorDefaultBookingService,
  resolveReserveServiceId,
  isProductType,
  isServiceType,
  isClassifiedType,
  type MarketplaceSeller,
  type MarketplaceAnnonce,
  type VendorBanner,
  type VendorCategoryRef,
  type VendorBookingService,
} from '@/lib/marketplaceCatalog';

// ─────────────────────────────────────────────────────────────
// Page publique « Boutique d'un vendeur » (marketplace).
// Affiche le profil du vendeur, ses bannières publicitaires,
// puis ses contenus séparés en 3 blocs repliables :
//   • Produits  • Services  • Annonces
// (tout est déplié par défaut).
// ─────────────────────────────────────────────────────────────

const SOCIAL_NETWORKS: { key: keyof MarketplaceSeller; icon: string; label: string }[] = [
  { key: 'lienfacebook', icon: 'ri-facebook-circle-line', label: 'Facebook' },
  { key: 'lieninstagram', icon: 'ri-instagram-line', label: 'Instagram' },
  { key: 'lientiktok', icon: 'ri-tiktok-line', label: 'TikTok' },
  { key: 'lientwitter', icon: 'ri-twitter-x-line', label: 'X' },
  { key: 'lienlinkedin', icon: 'ri-linkedin-box-line', label: 'LinkedIn' },
  { key: 'lienyoutube', icon: 'ri-youtube-line', label: 'YouTube' },
];

export default function VendorShopPage() {
  const { id } = useParams<{ id: string }>();
  const vendorId = Number(id);
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const storeName = resolveStoreName(tenant, theme);

  const [seller, setSeller] = useState<MarketplaceSeller | null>(null);
  const [banners, setBanners] = useState<VendorBanner[]>([]);
  const [annonces, setAnnonces] = useState<MarketplaceAnnonce[]>([]);
  const [declaredCat, setDeclaredCat] = useState<{ id: number | null; nom: string | null }>({ id: null, nom: null });
  const [declaredSub, setDeclaredSub] = useState<{ id: number | null; nom: string | null }>({ id: null, nom: null });
  const [declaredSubSub, setDeclaredSubSub] = useState<{ id: number | null; nom: string | null }>({ id: null, nom: null });
  const [productCats, setProductCats] = useState<VendorCategoryRef[]>([]);
  const [categoryNames, setCategoryNames] = useState<Record<number, string>>({});
  const [networks, setNetworks] = useState<Reseau[]>([]);
  const [rating, setRating] = useState<VendorRating | null>(null);
  const [bookingServices, setBookingServices] = useState<VendorBookingService[]>([]);
  const [defaultBookingId, setDefaultBookingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!Number.isFinite(vendorId) || vendorId <= 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [list, ann, bann, prodCats, cats] = await Promise.all([
          fetchSellersByIds([vendorId]),
          fetchVendorPublishedAnnonces(vendorId),
          merchantId ? fetchVendorBanners(merchantId, vendorId) : Promise.resolve([]),
          fetchVendorProductCategories(vendorId),
          merchantId ? fetchMarketplaceCategories(merchantId) : Promise.resolve([]),
        ]);
        const [rel, nets, book, defaultBook] = await Promise.all([
          merchantId ? fetchVendorRelation(merchantId, vendorId) : Promise.resolve(null),
          fetchSellerNetworks(vendorId),
          fetchVendorBookingServices(vendorId),
          fetchVendorDefaultBookingService(vendorId),
        ]);
        if (cancelled) return;
        setSeller(list[0] ?? null);
        setAnnonces(ann);
        setBanners(bann);
        setProductCats(prodCats);
        setCategoryNames(Object.fromEntries(cats.map((c) => [c.id, c.nom])) as Record<number, string>);
        setNetworks(nets);
        setBookingServices(book);
        setDefaultBookingId(defaultBook);
        setDeclaredCat({ id: rel?.categorieId ?? null, nom: rel?.categorieNom ?? null });
        setDeclaredSub({ id: rel?.souscategorieId ?? null, nom: rel?.souscategorieNom ?? null });
        setDeclaredSubSub({ id: rel?.soussouscategorieId ?? null, nom: rel?.soussouscategorieNom ?? null });
        const rated = await fetchVendorRatings([vendorId]);
        if (!cancelled) setRating(rated[vendorId] ?? { average: 0, count: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [vendorId, merchantId]);

  useEffect(() => {
    document.title = seller ? `${seller.nomcommerce} — ${storeName}` : `Boutique — ${storeName}`;
  }, [seller, storeName]);

  const sellerImg = resolveMediaUrl(seller?.image);
  const hasHours = hasOpeningHours(seller?.horairesJson);
  const openState = hasHours ? getOpenState(seller?.horairesJson) : null;
  const structuredHours = hasHours && seller?.horairesJson ? normalizeOpeningHours(seller.horairesJson) : null;
  const phoneDigits = cleanWhatsAppNumber(seller?.telephone || '');
  const whatsappUrl = phoneDigits
    ? buildWhatsAppUrl(phoneDigits, `Bonjour ${seller?.nomcommerce || ''}, je vous contacte via ${storeName}.`)
    : '';

  const visibleBanners = useMemo(() => banners.filter((b) => b.image || b.titre), [banners]);

  const query = search.trim().toLowerCase();
  const matchesSearch = (a: MarketplaceAnnonce): boolean =>
    !query || [a.titre, a.description].filter(Boolean).join(' ').toLowerCase().includes(query);

  // Annonce « à la une » choisie par le vendeur (unique), affichée en tête.
  // Masquée pendant une recherche pour ne montrer que les résultats pertinents.
  const featuredAnnonce = useMemo(
    () => (query ? null : annonces.find((a) => a.is_vendor_featured) ?? null),
    [annonces, query],
  );

  // Contenus hors « à la une », répartis par nature (produits / services / annonces).
  const rest = useMemo(() => {
    const base = featuredAnnonce ? annonces.filter((a) => a.id !== featuredAnnonce.id) : annonces;
    return base.filter(matchesSearch);
  }, [annonces, featuredAnnonce, query]);
  const produits = useMemo(() => rest.filter((a) => isProductType(a.type)), [rest]);
  const services = useMemo(() => rest.filter((a) => isServiceType(a.type)), [rest]);
  const classified = useMemo(() => rest.filter((a) => isClassifiedType(a.type)), [rest]);

  const socialLinks = useMemo(() => {
    if (!seller) return [];
    return SOCIAL_NETWORKS
      .map((n) => ({ ...n, url: String(seller[n.key] || '').trim() }))
      .filter((n) => /^https?:\/\//i.test(n.url));
  }, [seller]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 text-center">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-store-2-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Boutique introuvable</h2>
        <p className="text-sm text-foreground-500 mb-6">Ce vendeur n&apos;existe pas ou n&apos;est plus disponible.</p>
        <Link
          to="/vendeurs"
          className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          Voir les vendeurs
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-10">
        {/* Fil d'ariane */}
        <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/vendeurs" className="hover:text-foreground-700 no-underline whitespace-nowrap">Vendeurs</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600 truncate max-w-[220px]">{seller.nomcommerce}</span>
        </nav>

        {/* Carte boutique */}
        <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 md:p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="w-20 h-20 rounded-2xl bg-background-100 overflow-hidden flex items-center justify-center flex-shrink-0">
              {sellerImg ? (
                <img src={sellerImg} alt={seller.nomcommerce} className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-bold text-foreground-400 font-heading">
                  {seller.nomcommerce.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-1">{seller.nomcommerce}</h1>
              {(openState || networks.length > 0) && (
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  {openState && (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${openState.open ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${openState.open ? 'bg-green-500' : 'bg-red-500'}`}></span>
                      {openState.open ? 'Ouvert maintenant' : 'Fermé maintenant'}
                    </span>
                  )}
                  {networks.map((n) => (
                    <span key={n.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-accent-100 text-accent-900" title={n.description || undefined}>
                      <i className="ri-shield-check-line"></i>
                      Membre du réseau {n.titre}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 flex-wrap text-sm text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-store-2-line text-primary-500"></i>
                  Vendeur sur {storeName}
                </span>
                {(seller.ville || seller.pays) && (
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-map-pin-line text-primary-500"></i>
                    {[seller.ville, seller.pays].filter(Boolean).join(', ')}
                  </span>
                )}
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-shopping-bag-3-line text-primary-500"></i>
                  {annonces.length} contenu{annonces.length > 1 ? 's' : ''}
                </span>
              </div>
              {seller.description && (
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">{seller.description}</p>
              )}

              {(declaredCat.nom || declaredSub.nom || declaredSubSub.nom || productCats.length > 0) && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <span className="text-[11px] text-foreground-400 flex items-center gap-1">
                    <i className="ri-price-tag-3-line"></i>
                    Catégories
                  </span>
                  {declaredCat.nom && (
                    <Link
                      to={declaredCat.id ? `/marketplace/categorie/${declaredCat.id}` : '/categories'}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold no-underline hover:bg-primary-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-star-fill text-[9px]"></i>
                      {declaredCat.nom}
                    </Link>
                  )}
                  {declaredSub.nom && (
                    <Link
                      to={declaredSub.id ? `/marketplace/categorie/${declaredSub.id}` : '/categories'}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-medium no-underline hover:bg-accent-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-price-tag-2-line text-[9px]"></i>
                      {declaredSub.nom}
                    </Link>
                  )}
                  {declaredSubSub.nom && (
                    <Link
                      to={declaredSubSub.id ? `/marketplace/categorie/${declaredSubSub.id}` : '/categories'}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium no-underline hover:bg-secondary-200 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-price-tag-2-line text-[9px]"></i>
                      {declaredSubSub.nom}
                    </Link>
                  )}
                  {productCats
                    .filter((c) => c.id !== declaredCat.id)
                    .map((c) => (
                      <Link
                        key={c.id}
                        to={`/marketplace/categorie/${c.id}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium no-underline hover:bg-secondary-200 transition-colors whitespace-nowrap"
                      >
                        {c.nom}
                      </Link>
                    ))}
                </div>
              )}

              {/* Réseaux sociaux */}
              {socialLinks.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {socialLinks.map((s) => (
                    <a
                      key={s.key}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.label}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 no-underline hover:bg-primary-100 hover:text-primary-600 transition-colors"
                    >
                      <i className={s.icon}></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              {bookingServices.length > 0 && (
                <Link
                  to={`/booking?vendeur=${vendorId}`}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-semibold no-underline hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line"></i>
                  Réserver
                </Link>
              )}
              {whatsappUrl && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-green-500 text-white text-sm font-semibold no-underline hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  WhatsApp
                </a>
              )}
              {seller.telephone && (
                <a
                  href={`tel:${seller.telephone}`}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  Appeler
                </a>
              )}
            </div>
          </div>

          {/* Horaires d'ouverture (créneaux structurés) */}
          {hasHours && structuredHours && (
            <div className="mt-5 pt-5 border-t border-background-200/70">
              <h2 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-3">
                <i className="ri-time-line text-primary-500"></i>
                Horaires d&apos;ouverture
                {openState && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${openState.open ? 'bg-green-100 text-green-700' : 'bg-red-50 text-red-600'}`}>
                    {openState.open ? 'Ouvert' : 'Fermé'}
                  </span>
                )}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                {WEEKDAYS.map((d) => {
                  const day = structuredHours[d.key];
                  const closed = !day || day.closed;
                  const isToday = new Date().getDay() === d.jsDay;
                  return (
                    <div
                      key={d.key}
                      className={`flex items-center justify-between gap-3 text-sm py-0.5 ${isToday ? 'font-semibold text-foreground-900' : 'text-foreground-600'}`}
                    >
                      <span className="flex items-center gap-2">
                        {isToday && <span className="w-1.5 h-1.5 rounded-full bg-primary-500"></span>}
                        {d.label}
                      </span>
                      <span className={closed ? 'text-foreground-400' : ''}>
                        {closed ? 'Fermé' : `${day.open} – ${day.close}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Recherche dans la boutique */}
        {annonces.length > 0 && (
          <div className="mb-6">
            <div className="relative w-full sm:max-w-md">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un produit, service ou annonce…"
                className="w-full pl-10 pr-10 py-2.5 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-700 transition-colors cursor-pointer"
                  aria-label="Effacer la recherche"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
            {query && (
              <p className="text-xs text-foreground-500 mt-2">
                <i className="ri-filter-3-line mr-1"></i>
                {produits.length + services.length + classified.length} résultat{produits.length + services.length + classified.length > 1 ? 's' : ''} pour « {search.trim()} »
              </p>
            )}
          </div>
        )}

        {/* Bannières publicitaires du vendeur */}
        {visibleBanners.length > 0 && (
          <section className="mb-10">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-image-2-line text-primary-500"></i>
              À la une de la boutique
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {visibleBanners.map((b) => (
                <BannerCard key={b.id} banner={b} />
              ))}
            </div>
          </section>
        )}

        {/* Annonce à la une du vendeur */}
        {featuredAnnonce && (
          <section className="mb-10">
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-star-fill text-accent-500"></i>
              Contenu à la une
            </h2>
            <div className="rounded-xl border border-accent-200 bg-accent-50/50 p-4 md:p-5">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
                <AnnonceCard
                  annonce={featuredAnnonce}
                  monaie={monaie}
                  sellerName={seller?.nomcommerce}
                  sellerRating={rating}
                  categoryNames={categoryNames}
                  reserveHref={
                    resolveReserveServiceId(featuredAnnonce, bookingServices, defaultBookingId)
                      ? `/booking/${resolveReserveServiceId(featuredAnnonce, bookingServices, defaultBookingId)}`
                      : undefined
                  }
                />
              </div>
            </div>
          </section>
        )}

        {/* Contenus séparés par nature, blocs repliables (dépliés par défaut) */}
        {annonces.length === 0 ? (
          <section>
            <h2 className="text-lg font-bold font-heading text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-shopping-bag-3-line text-primary-500"></i>
              Les contenus de {seller.nomcommerce}
            </h2>
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-shopping-bag-line text-xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">Ce vendeur n&apos;a pas encore publié de contenu.</p>
            </div>
          </section>
        ) : query && produits.length + services.length + classified.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
              <i className="ri-search-line text-xl text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Aucun contenu ne correspond à votre recherche.</p>
          </div>
        ) : (
          <div className="space-y-6">
            <CollapsibleSection
              icon="ri-shopping-bag-3-line"
              title="Produits"
              count={produits.length}
              tone="bg-primary-100 text-primary-700"
            >
              <AnnonceGrid list={produits} monaie={monaie} emptyText="Aucun produit publié pour le moment." sellerName={seller.nomcommerce} sellerRating={rating} categoryNames={categoryNames} />
            </CollapsibleSection>

            <CollapsibleSection
              icon="ri-service-line"
              title="Services"
              count={services.length}
              tone="bg-secondary-100 text-secondary-900"
            >
              <AnnonceGrid
                list={services}
                monaie={monaie}
                emptyText="Aucun service publié pour le moment."
                sellerName={seller.nomcommerce}
                sellerRating={rating}
                categoryNames={categoryNames}
                bookingServices={bookingServices}
                defaultBookingId={defaultBookingId}
              />
            </CollapsibleSection>

            <CollapsibleSection
              icon="ri-megaphone-line"
              title="Annonces"
              count={classified.length}
              tone="bg-accent-100 text-accent-900"
            >
              <AnnonceGrid list={classified} monaie={monaie} emptyText="Aucune annonce publiée pour le moment." sellerName={seller.nomcommerce} sellerRating={rating} categoryNames={categoryNames} />
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
}

function AnnonceGrid({
  list,
  monaie,
  emptyText,
  sellerName,
  sellerRating,
  categoryNames,
  bookingServices,
  defaultBookingId,
}: {
  list: MarketplaceAnnonce[];
  monaie: string;
  emptyText: string;
  sellerName?: string;
  sellerRating?: VendorRating | null;
  categoryNames?: Record<number, string>;
  bookingServices?: VendorBookingService[];
  defaultBookingId?: number | null;
}) {
  if (list.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-background-200/70 py-10 text-center">
        <p className="text-sm text-foreground-400">{emptyText}</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
      {list.map((a) => {
        const reserveId = bookingServices
          ? resolveReserveServiceId(a, bookingServices, defaultBookingId ?? null)
          : null;
        return (
          <AnnonceCard
            key={a.id}
            annonce={a}
            monaie={monaie}
            sellerName={sellerName}
            sellerRating={sellerRating}
            categoryNames={categoryNames}
            reserveHref={reserveId ? `/booking/${reserveId}` : undefined}
          />
        );
      })}
    </div>
  );
}

function CollapsibleSection({
  icon,
  title,
  count,
  tone,
  children,
}: {
  icon: string;
  title: string;
  count: number;
  tone: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section className="border border-background-200/70 rounded-xl overflow-hidden bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 md:px-5 py-4 hover:bg-background-100/60 transition-colors cursor-pointer"
        aria-expanded={open}
      >
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
          <i className={`${icon} text-lg`}></i>
        </span>
        <span className="text-base font-bold font-heading text-foreground-950">{title}</span>
        <span className="min-w-[22px] h-[22px] px-1.5 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 text-[11px] font-semibold">
          {count}
        </span>
        <span className="flex-1"></span>
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 text-lg`}></i>
      </button>
      {open && (
        <div className="px-4 md:px-5 pb-5">
          {children}
        </div>
      )}
    </section>
  );
}

function BannerCard({ banner }: { banner: VendorBanner }) {
  const img = resolveMediaUrl(banner.image);
  const content = (
    <div
      className="relative rounded-xl overflow-hidden border border-background-200/70 bg-background-100 h-40 md:h-52"
    >
      {img ? (
        <img src={img} alt={banner.titre || 'Bannière'} className="w-full h-full object-cover object-top" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <i className="ri-image-line text-3xl text-foreground-300"></i>
        </div>
      )}
      {(banner.titre || banner.description) && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent flex flex-col justify-end p-4">
          {banner.titre && <h3 className="text-base md:text-lg font-bold text-white">{banner.titre}</h3>}
          {banner.description && <p className="text-xs text-white/80 line-clamp-1">{banner.description}</p>}
        </div>
      )}
    </div>
  );

  if (banner.idannonce > 0) {
    return (
      <Link
        to={`/annonce/${banner.idannonce}`}
        onClick={() => { void incrementBannerClick(banner.id, banner.click); }}
        className="block no-underline cursor-pointer"
      >
        {content}
      </Link>
    );
  }
  return content;
}